import { Link, router } from '@inertiajs/react'
import axios from 'axios'
import {
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  Textarea,
  Title,
} from '@mantine/core'
import {
  IconArrowLeft,
  IconCheck,
  IconCode,
  IconMessageExclamation,
  IconRefresh,
  IconRocket,
} from '@tabler/icons-react'
import { useCallback, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { StudioLayout } from '../Components/Layouts'
import { SchemaForm } from './components/forms/SchemaForm'
import { EditorShell } from './components/layout/EditorShell'
import { SectionCard } from './components/layout/SectionCard'
import { LivePreview } from './components/preview/LivePreview'
import { TemplateSetup } from './components/templates/TemplateSetup'
import { useTemplateReview } from './hooks/useTemplateReview'
import classes from './styles/StudioEditorV2.module.css'

function actionTitle(action) {
  if (action === 'changes') return 'Request template changes'
  if (action === 'refresh') return 'Refresh automated evidence'
  return 'Promote immutable template release'
}

function ReviewDecision({ review, previewState }) {
  const [action, setAction] = useState(null)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState(null)
  const [operationError, setOperationError] = useState(null)
  const approveMutation = useMutation({
    mutationFn: () => axios.post(review.approveUrl, { confirmed: true }),
    networkMode: 'always',
    onSuccess: () => router.reload({ preserveScroll: true }),
    onError: (requestError) => {
      setOperationError(requestError?.response?.data?.message || 'The template could not be approved.')
    },
  })
  const installMutation = useMutation({
    mutationFn: () => axios.post(review.installUrl),
    networkMode: 'always',
    onSuccess: (response) => window.location.assign(response.data.data.createAdUrl),
    onError: (requestError) => {
      setOperationError(requestError?.response?.data?.message || 'The template could not be installed in the editor.')
    },
  })
  const actionMutation = useMutation({
    mutationFn: ([url, body]) => axios.post(url, body),
    networkMode: 'always',
    onSuccess: () => {
      setAction(null)
      router.reload({ preserveScroll: true })
    },
    onError: (requestError) => {
      const errors = requestError?.response?.data?.errors
      setError(errors?.notes?.[0] || requestError?.response?.data?.message || 'The review action failed.')
    },
  })
  const approving = approveMutation.isPending
  // A successful install leaves the page, so the button stays busy until it does.
  const installing = installMutation.isPending || installMutation.isSuccess
  const submitting = actionMutation.isPending
  const awaiting = review.state.status === 'in-review'
  const previewReady = previewState.status === 'ready' && !previewState.error
  const canApprove = awaiting && review.quality.current && previewReady
  const canPromote = review.state.status === 'approved'

  function openAction(nextAction) {
    setAction(nextAction)
    setNotes('')
    setError(null)
  }

  function approve() {
    if (!window.confirm('Approve this template package?')) return
    setOperationError(null)
    approveMutation.mutate()
  }

  function installAndOpen() {
    setOperationError(null)
    installMutation.mutate()
  }

  function submit() {
    if (!action || notes.trim().length < 10) {
      setError('Enter at least 10 characters of useful review notes.')
      return
    }
    const requests = {
      changes: [review.requestChangesUrl, { notes: notes.trim() }],
      refresh: [review.refreshQualityUrl, { notes: notes.trim() }],
      promote: [review.promoteUrl, { notes: notes.trim(), confirmed: true }],
    }
    setError(null)
    actionMutation.mutate(requests[action])
  }

  return (
    <Paper withBorder radius="md" p="sm" className={classes.previewSaveAction}>
      <Stack gap="xs">
        <Group justify="space-between">
          <Text size="sm" fw={700}>Human decision</Text>
          <Badge color={awaiting ? 'yellow' : canPromote ? 'green' : review.state.status === 'promoted' ? 'blue' : 'gray'}>
            {review.state.status}
          </Badge>
        </Group>
        {!review.quality.current && awaiting && (
          <Alert color="orange" py="xs" title="Evidence is stale">
            Run quality-v2 before approval.
          </Alert>
        )}
        {awaiting && (
          <Group grow>
            <Button
              size="sm"
              color="green"
              leftSection={<IconCheck size={16} />}
              disabled={!canApprove}
              loading={approving}
              onClick={approve}
            >
              Approve
            </Button>
            <Button size="sm" color="red" variant="light" leftSection={<IconMessageExclamation size={16} />} onClick={() => openAction('changes')}>
              Changes
            </Button>
          </Group>
        )}
        {awaiting && !review.quality.current && (
          <Button size="sm" variant="light" leftSection={<IconRefresh size={16} />} onClick={() => openAction('refresh')}>
            Refresh automated evidence
          </Button>
        )}
        {canPromote && (
          <Button size="sm" color="pink" leftSection={<IconRocket size={16} />} onClick={() => openAction('promote')}>
            Promote release
          </Button>
        )}
        {review.editorAvailable && (
          <Button component={Link} href={review.createAdUrl} size="sm" color="pink">
            Create ad with this template
          </Button>
        )}
        {review.state.status === 'promoted' && !review.editorAvailable && (
          <Button size="sm" color="pink" loading={installing} onClick={installAndOpen}>
            Install and create ad
          </Button>
        )}
        {operationError && <Alert color="red" py="xs">{operationError}</Alert>}
        <Text size="xs" c={previewReady ? 'green' : 'dimmed'}>
          Live runtime: {previewState.status}{previewState.error ? ` · ${previewState.error.message}` : ''}
        </Text>
      </Stack>
      <Modal
        opened={Boolean(action)}
        onClose={() => !submitting && setAction(null)}
        title={actionTitle(action)}
        centered
      >
        <Stack>
          <Text size="sm">
            {action === 'changes' && 'Describe exactly what the author must change before resubmitting.'}
            {action === 'refresh' && 'This reruns current gates for the same submitted hash and preserves the author attestation.'}
            {action === 'promote' && 'This creates the immutable release. It cannot overwrite or revise an existing semantic release.'}
          </Text>
          <Text size="xs" ff="monospace" c="dimmed" style={{ wordBreak: 'break-all' }}>{review.packageSha256}</Text>
          <Textarea
            label="Audit notes"
            minRows={5}
            maxLength={1600}
            value={notes}
            onChange={(event) => setNotes(event.currentTarget.value)}
            error={error}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAction(null)} disabled={submitting}>Cancel</Button>
            <Button
              color={action === 'changes' ? 'red' : 'pink'}
              onClick={submit}
              loading={submitting}
            >
              {action === 'changes' && 'Send change request'}
              {action === 'refresh' && 'Run current gates'}
              {action === 'promote' && 'Promote immutable release'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  )
}

function Evidence({ review }) {
  const screenshots = review.screenshots || []
  return (
    <SectionCard title="Automated evidence" description="Each image and runtime measurement is pinned to the exact submitted package hash.">
      <Stack gap="sm">
        <Group gap="xs">
          <Badge color={review.quality.current ? 'green' : 'orange'}>{review.quality.status}</Badge>
          <Badge variant="light">{review.quality.reportContractVersion || 'legacy report'}</Badge>
          <Text size="xs" c="dimmed">{review.quality.scenarioCount} scenarios</Text>
        </Group>
        {screenshots.length ? (
          <Tabs defaultValue={screenshots[0].scenarioId} variant="outline">
            <Tabs.List>
              {screenshots.map((shot) => (
                <Tabs.Tab key={shot.scenarioId} value={shot.scenarioId}>
                  {shot.width} × {shot.height}{shot.presetId ? ` · ${shot.presetId}` : ''}
                </Tabs.Tab>
              ))}
            </Tabs.List>
            {screenshots.map((shot) => (
              <Tabs.Panel key={shot.scenarioId} value={shot.scenarioId} pt="md">
                <Stack gap="xs">
                  <Paper withBorder p="sm" bg="gray.0" style={{ overflow: 'auto' }}>
                    <Group justify="center" wrap="nowrap">
                      <Image src={shot.url} alt={`${shot.scenarioId} quality screenshot`} w="auto" maw="100%" fit="contain" />
                    </Group>
                  </Paper>
                  <Text size="xs" c="dimmed">
                    Visible patch: {shot.probePath || 'legacy gate'}
                    {shot.maxPatchMs != null ? ` · maximum ${shot.maxPatchMs.toFixed(3)} ms` : ''}
                    {shot.visibleTextChars != null ? ` · ${shot.visibleTextChars} visible text characters` : ''}
                  </Text>
                </Stack>
              </Tabs.Panel>
            ))}
          </Tabs>
        ) : <Alert color="yellow">No current screenshot evidence is attached to this workspace.</Alert>}
      </Stack>
    </SectionCard>
  )
}

function SourceInspector({ files }) {
  const [selected, setSelected] = useState(files?.[0]?.path || null)
  const file = (files || []).find((entry) => entry.path === selected)
  const sourceQuery = useQuery({
    queryKey: ['studio-editor-v2', 'review-source', file?.url],
    queryFn: async ({ signal }) => (await axios.get(file.url, { signal })).data.data,
    enabled: Boolean(file),
    retry: false,
    gcTime: 0,
    networkMode: 'always',
    refetchOnReconnect: false,
  })
  const source = sourceQuery.isFetching || sourceQuery.isError ? null : (sourceQuery.data ?? null)
  const requestError = sourceQuery.isError && !sourceQuery.isFetching ? sourceQuery.error : null
  const status = requestError?.response?.status
  const error = requestError
    ? requestError?.response?.data?.message || `${file?.path} could not be loaded${status ? ` (HTTP ${status})` : ''}.`
    : null

  return (
    <SectionCard title="Read-only package source" description="Source is escaped, hash-pinned, and cannot be changed from the reviewer UI.">
      <Stack gap="sm">
        <Group gap="xs">
          {(files || []).map((file) => (
            <Button key={file.path} size="compact-xs" variant={selected === file.path ? 'filled' : 'light'} onClick={() => setSelected(file.path)}>
              {file.path}
            </Button>
          ))}
        </Group>
        {error && (
          <Alert color="red">
            <Group justify="space-between" align="center">
              <Text size="sm">{error}</Text>
              <Button size="compact-xs" color="red" variant="light" onClick={() => sourceQuery.refetch()}>
                Retry
              </Button>
            </Group>
          </Alert>
        )}
        {!source && !error && <Loader size="sm" />}
        {source && (
          <Stack gap={4}>
            <Text size="xs" ff="monospace" c="dimmed">{source.path} · {source.bytes} bytes · {source.sha256}</Text>
            <ScrollArea h={520} type="auto">
              <Code block style={{ whiteSpace: 'pre', minWidth: 'max-content' }}>{source.content}</Code>
            </ScrollArea>
          </Stack>
        )}
      </Stack>
    </SectionCard>
  )
}

export default function TemplateReviewPage({ templateReview }) {
  const editor = useTemplateReview(templateReview.templateBundle)
  const [previewState, setPreviewState] = useState({ status: 'loading', error: null })
  const onPreviewStatus = useCallback((next) => setPreviewState(next), [])
  const attestation = templateReview.state.authorAttestation || {}
  const changeRequest = templateReview.state.changeRequest
  const approval = templateReview.state.approval

  return (
    <StudioLayout pageTitle={`Review ${templateReview.state.releaseKey}`}>
      <EditorShell
        header={(
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <div>
                <Button component={Link} href={templateReview.indexUrl} variant="subtle" color="gray" size="compact-sm" leftSection={<IconArrowLeft size={15} />} px={0}>
                  Template reviews
                </Button>
                <Title order={1} size="h3" mt={4}>{editor.template.manifest.name}</Title>
                <Group gap="xs" mt={5}>
                  <Badge variant="light">{templateReview.state.releaseKey}</Badge>
                  <Badge color="yellow" variant="light">private workspace</Badge>
                  <Badge color={templateReview.state.status === 'approved' ? 'green' : templateReview.state.status === 'promoted' ? 'blue' : 'gray'}>{templateReview.state.status}</Badge>
                  <Badge color={templateReview.quality.current ? 'green' : 'orange'}>quality {templateReview.quality.status}</Badge>
                </Group>
              </div>
              <Text size="xs" ff="monospace" c="dimmed" maw={430} style={{ wordBreak: 'break-all' }}>
                SHA-256 {templateReview.packageSha256}
              </Text>
            </Group>
            <Alert color="blue" title="Safe interactive review">
              Test values stay in this browser. The iframe runs the exact submitted HTML, CSS, and JavaScript; package source remains immutable.
            </Alert>
          </Stack>
        )}
        editor={(
          <Tabs defaultValue="interactive" keepMounted={false}>
            <Tabs.List mb="md">
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              <Tabs.Tab value="evidence">Evidence</Tabs.Tab>
              <Tabs.Tab value="interactive">Interactive test</Tabs.Tab>
              <Tabs.Tab value="source" leftSection={<IconCode size={14} />}>Source</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="overview">
              <SectionCard title="Review submission" description={`Submitted by ${attestation.principalId || templateReview.state.author || 'unknown author'}.`}>
                <Stack gap="sm">
                  <div><Text size="xs" fw={700} c="dimmed">AUTHOR SUMMARY</Text><Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{attestation.summary || 'No summary supplied.'}</Text></div>
                  <div><Text size="xs" fw={700} c="dimmed">KNOWN LIMITATIONS</Text><Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{attestation.limitations || 'None supplied.'}</Text></div>
                  {changeRequest && <Alert color="red" title="Changes requested" style={{ whiteSpace: 'pre-wrap' }}>{changeRequest.notes}</Alert>}
                  {approval && <Alert color="green" title="Approved" style={{ whiteSpace: 'pre-wrap' }}>{approval.notes}</Alert>}
                </Stack>
              </SectionCard>
            </Tabs.Panel>
            <Tabs.Panel value="evidence"><Evidence review={templateReview} /></Tabs.Panel>
            <Tabs.Panel value="interactive">
              <Stack gap="md">
                <Group justify="flex-end">
                  <Button size="compact-sm" variant="light" leftSection={<IconRefresh size={14} />} onClick={editor.reset}>
                    Reset test values
                  </Button>
                </Group>
                <TemplateSetup manifest={editor.template.manifest} schema={editor.template.schema} presets={editor.template.presets} configuration={editor.configuration} updateValue={editor.updateValue} />
                {editor.assetError && <Alert color="red">{editor.assetError}</Alert>}
                <SchemaForm
                  schema={editor.template.schema}
                  templateManifest={editor.template.manifest}
                  configuration={editor.configuration}
                  errors={editor.errors}
                  updateValue={editor.updateValue}
                  addAsset={editor.addAsset}
                  addRenditionPreview={editor.addRenditionPreview}
                  assetUrls={editor.assetUrls}
                  assetSourceUrls={editor.assetSourceUrls}
                  selectedItems={editor.selectedItems}
                  setSelectedItem={editor.setSelectedItem}
                  addCollectionItem={editor.addCollectionItem}
                  removeCollectionItem={editor.removeCollectionItem}
                  moveCollectionItem={editor.moveCollectionItem}
                  duplicateCollectionItem={editor.duplicateCollectionItem}
                />
              </Stack>
            </Tabs.Panel>
            <Tabs.Panel value="source"><SourceInspector files={templateReview.sourceFiles} /></Tabs.Panel>
          </Tabs>
        )}
        preview={(
          <Stack gap="xs">
            <ReviewDecision review={templateReview} previewState={previewState} />
            <LivePreview
              previewKey={templateReview.previewKey}
              previewUrl={templateReview.previewUrl}
              configuration={editor.previewConfiguration}
              selectedItems={editor.previewSelections}
              onSelectItem={editor.selectPreviewItem}
              protocolVersion={editor.template.manifest.previewProtocolVersion || 2}
              onStatusChange={onPreviewStatus}
            />
          </Stack>
        )}
      />
    </StudioLayout>
  )
}
